<template>
  <div class="ha">
    <div class="ha__dev-banner" role="status">
      <span class="ha__dev-badge">In development</span>
      <span class="ha__dev-text">
        Analytics is still being refined — figures may change as we improve
        calculations and coverage.
      </span>
    </div>

    <div class="ha__hero">
      <div class="ha__hero-copy">
        <p class="ha__eyebrow">Organizer insights</p>
        <h2 class="ha__title">Analytics</h2>
        <p class="ha__lede">
          Sales, bookings, vouchers and attendance for the selected period.
        </p>
      </div>
      <div class="ha__hero-meta">
        <span class="ha__chip">{{ analytics.periodLabel || "—" }}</span>
        <span class="ha__chip ha__chip--dates">{{ periodRangeLabel }}</span>
        <span class="ha__chip ha__chip--muted">JOD</span>
      </div>
    </div>

    <div class="ha__toolbar">
      <div class="ha__presets" role="tablist">
        <button
          v-for="opt in presetOptions"
          :key="opt.value"
          type="button"
          class="ha__preset"
          :class="{ 'ha__preset--active': preset === opt.value }"
          @click="selectPreset(opt.value)"
        >
          {{ opt.text }}
        </button>
      </div>

      <div class="ha__range-note">
        <span class="ha__range-note-label">Dates considered</span>
        <span class="ha__range-note-value">{{ periodRangeLabel }}</span>
      </div>

      <div class="ha__toolbar-fields">
        <div v-if="preset === 'custom'" class="ha__date-pair">
          <flat-pickr
            v-model="startDate"
            class="form-control ha__input"
            placeholder="From"
            :config="dateConfig"
          />
          <span class="ha__date-sep">to</span>
          <flat-pickr
            v-model="endDate"
            class="form-control ha__input"
            placeholder="To"
            :config="dateConfig"
          />
        </div>
        <div class="ha__event-field">
          <label class="ha__field-label" for="ha-event-select">Event</label>
          <select
            id="ha-event-select"
            v-model="selectedEventId"
            class="form-control ha__input ha__event-native"
          >
            <option value="">All events</option>
            <option
              v-for="ev in eventOptions"
              :key="ev.event_id"
              :value="ev.event_id"
            >
              {{ ev.event_name }}
            </option>
          </select>
        </div>
        <button
          type="button"
          class="ha__btn ha__btn--primary"
          :disabled="loading"
          @click="loadAnalytics"
        >
          Apply
        </button>
        <button
          type="button"
          class="ha__btn ha__btn--ghost"
          :disabled="loading"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>
    </div>

    <div v-if="loading" class="ha__loading">
      <div class="ha__loading-bar" />
      <span>Refreshing metrics…</span>
    </div>

    <template v-else>
      <div v-if="(analytics.alerts || []).length" class="ha__insights">
        <div
          v-for="(a, idx) in analytics.alerts"
          :key="'insight-' + idx"
          class="ha__insight"
          :class="'ha__insight--' + (a.variant || 'info')"
        >
          <span class="ha__insight-dot" />
          <span>{{ a.text }}</span>
        </div>
      </div>

      <section class="ha__sales-card">
        <div class="ha__sales-card-head">
          <div>
            <h3 class="ha__sales-card-title">Sales Summary</h3>
            <p class="ha__sales-card-sub">
              {{ salesSummarySubtitle }}
            </p>
          </div>
        </div>
        <div class="ha__sales-table-wrap">
          <table class="ha__sales-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Event Name</th>
                <th>Event Date</th>
                <th>Schedule Time</th>
                <th class="ha__num">Total Transactions</th>
                <th class="ha__num">Total Seats Booked</th>
                <th class="ha__num">Voucher Bookings</th>
                <th class="ha__num">Transaction Amount</th>
                <th class="ha__num">Total Refunds</th>
                <th class="ha__num">Refund Amount</th>
                <th class="ha__num">Final Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in salesSummaryRows"
                :key="'ss-' + idx"
              >
                <td>{{ row.eventType }}</td>
                <td class="ha__sales-name">{{ row.eventName }}</td>
                <td>{{ row.eventDate }}</td>
                <td>{{ row.eventTime }}</td>
                <td class="ha__num">{{ formatNumber(row.totalTransactions) }}</td>
                <td class="ha__num">{{ formatNumber(row.totalSeatsBooked) }}</td>
                <td class="ha__num">{{ formatNumber(row.voucherBookings) }}</td>
                <td class="ha__num">{{ formatJod(row.transactionAmount, row.currency) }}</td>
                <td class="ha__num">{{ formatNumber(row.totalRefunds) }}</td>
                <td class="ha__num">{{ formatJod(row.refundAmount, row.currency) }}</td>
                <td class="ha__num">{{ formatJod(row.finalAmount, row.currency) }}</td>
              </tr>
              <tr v-if="!salesSummaryRows.length">
                <td colspan="11" class="ha__sales-empty">
                  No sales in this period
                </td>
              </tr>
            </tbody>
            <tfoot v-if="salesSummaryRows.length">
              <tr>
                <td colspan="4">Grand Total</td>
                <td class="ha__num">{{ formatNumber(salesSummaryTotals.totalTransactions) }}</td>
                <td class="ha__num">{{ formatNumber(salesSummaryTotals.totalSeatsBooked) }}</td>
                <td class="ha__num">{{ formatNumber(salesSummaryTotals.voucherBookings) }}</td>
                <td class="ha__num">{{ formatJod(salesSummaryTotals.transactionAmount) }}</td>
                <td class="ha__num">{{ formatNumber(salesSummaryTotals.totalRefunds) }}</td>
                <td class="ha__num">{{ formatJod(salesSummaryTotals.refundAmount) }}</td>
                <td class="ha__num">{{ formatJod(salesSummaryTotals.finalAmount) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section class="ha__section">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Sales &amp; revenue</h3>
          <p class="ha__section-sub">Paid bookings in this window</p>
        </div>

        <div class="ha__revenue-grid">
          <article class="ha__stat ha__stat--hero">
            <div class="ha__stat-label">Gross revenue</div>
            <div class="ha__stat-value ha__stat-value--xl">
              {{ formatCurrency(analytics.revenueGross) }}
            </div>
            <div
              class="ha__stat-delta"
              :class="
                analytics.salesDeltaPct >= 0
                  ? 'ha__stat-delta--up'
                  : 'ha__stat-delta--down'
              "
            >
              {{ analytics.salesDeltaPct >= 0 ? "+" : ""
              }}{{ analytics.salesDeltaPct || 0 }}% vs prior period
            </div>
          </article>
          <article class="ha__stat">
            <div class="ha__stat-label">Net after fees</div>
            <div class="ha__stat-value">
              {{ formatCurrency(analytics.revenueNet) }}
            </div>
          </article>
          <article class="ha__stat">
            <div class="ha__stat-label">Platform fees</div>
            <div class="ha__stat-value">
              {{ formatCurrency(analytics.platformFees) }}
            </div>
          </article>
        </div>

        <div class="ha__metric-grid">
          <article class="ha__metric">
            <div class="ha__metric-label">Tickets sold</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.ticketsSold) }}
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Avg order value</div>
            <div class="ha__metric-value">
              {{ formatCurrency(analytics.avgOrderValue) }}
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Unique buyers</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.uniqueBuyers) }}
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Repeat buyers</div>
            <div class="ha__metric-value">
              {{ analytics.repeatBuyerRate || 0 }}%
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Tickets / order</div>
            <div class="ha__metric-value">
              {{ analytics.avgTicketsPerOrder || 0 }}
            </div>
          </article>
        </div>
      </section>

      <section class="ha__section">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Upcoming inventory</h3>
          <p class="ha__section-sub">
            Sell-through across active upcoming schedules
          </p>
        </div>
        <div class="ha__metric-grid ha__metric-grid--4">
          <article class="ha__metric ha__metric--ok">
            <div class="ha__metric-label">Sell-through</div>
            <div class="ha__metric-value">
              {{ (analytics.occupancy && analytics.occupancy.sellThroughRate) || 0 }}%
            </div>
            <div class="ha__meter">
              <div
                class="ha__meter-fill ha__meter-fill--ok"
                :style="{
                  width:
                    ((analytics.occupancy &&
                      analytics.occupancy.sellThroughRate) ||
                      0) + '%',
                }"
              />
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Upcoming shows</div>
            <div class="ha__metric-value">
              {{
                formatNumber(
                  (analytics.occupancy &&
                    analytics.occupancy.upcomingSchedules) ||
                    0,
                )
              }}
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Seats sold</div>
            <div class="ha__metric-value">
              {{
                formatNumber(
                  (analytics.occupancy && analytics.occupancy.soldSeats) || 0,
                )
              }}
            </div>
            <div class="ha__metric-foot">
              of
              {{
                formatNumber(
                  (analytics.occupancy && analytics.occupancy.totalCapacity) ||
                    0,
                )
              }}
            </div>
          </article>
          <article class="ha__metric ha__metric--warn">
            <div class="ha__metric-label">Seats remaining</div>
            <div class="ha__metric-value">
              {{
                formatNumber(
                  (analytics.occupancy && analytics.occupancy.remainingSeats) ||
                    0,
                )
              }}
            </div>
          </article>
        </div>
        <div
          v-if="
            analytics.occupancy &&
            analytics.occupancy.nearSellOut &&
            analytics.occupancy.nearSellOut.length
          "
          class="ha__near-list"
        >
          <div class="ha__near-title">Near sell-out (85%+)</div>
          <div
            v-for="(row, idx) in analytics.occupancy.nearSellOut"
            :key="'near-' + idx"
            class="ha__near-row"
          >
            <span class="ha__near-name">{{ row.name }}</span>
            <span class="ha__near-meta"
              >{{ row.date }} {{ row.time }} · {{ row.fillRate }}%</span
            >
          </div>
        </div>
      </section>

      <div class="ha__split">
        <section class="ha__section ha__section--panel">
          <div class="ha__section-head">
            <h3 class="ha__section-title">Sales trend</h3>
            <p class="ha__section-sub">
              Peak {{ formatCurrency(salesTrendMax) }}
            </p>
          </div>
          <div class="ha__trend">
            <div
              v-for="(d, i) in analytics.salesTrend || []"
              :key="'trend-' + i"
              class="ha__trend-col"
              :title="formatCurrency(d.value)"
            >
              <div class="ha__trend-value">
                {{ formatCompact(d.value) }}
              </div>
              <div class="ha__trend-track">
                <div
                  class="ha__trend-bar"
                  :style="{
                    height: Math.max((d.value / salesTrendMax) * 100, 3) + '%',
                  }"
                />
              </div>
              <div class="ha__trend-label">{{ d.label }}</div>
            </div>
          </div>
        </section>

        <section class="ha__section ha__section--panel">
          <div class="ha__section-head">
            <h3 class="ha__section-title">Top event</h3>
            <p class="ha__section-sub">Highest revenue this period</p>
          </div>
          <div v-if="analytics.topPerformingEvent" class="ha__spotlight">
            <div class="ha__spotlight-rank">#1</div>
            <div class="ha__spotlight-body">
              <div class="ha__spotlight-name">
                {{ analytics.topPerformingEvent.name }}
              </div>
              <div class="ha__spotlight-stats">
                <span>{{
                  formatCurrency(analytics.topPerformingEvent.revenue)
                }}</span>
                <span class="ha__dot" />
                <span
                  >{{
                    formatNumber(analytics.topPerformingEvent.tickets)
                  }}
                  tickets</span
                >
              </div>
            </div>
          </div>
          <div v-else class="ha__empty">No paid bookings yet</div>

          <ul class="ha__rank-list">
            <li
              v-for="(row, idx) in analytics.topEvents || []"
              :key="'top-' + idx"
              class="ha__rank-item"
            >
              <span class="ha__rank-num">{{ idx + 1 }}</span>
              <span class="ha__rank-name">{{ row.name }}</span>
              <span class="ha__rank-rev">{{ formatCurrency(row.revenue) }}</span>
            </li>
          </ul>
        </section>
      </div>

      <section class="ha__section">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Bookings &amp; payments</h3>
          <p class="ha__section-sub">Cancellations, failures and refunds</p>
        </div>
        <div class="ha__metric-grid ha__metric-grid--4">
          <article class="ha__metric ha__metric--warn">
            <div class="ha__metric-label">Cancelled</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.cancelledBookings) }}
            </div>
            <div class="ha__metric-foot">
              {{ formatCurrency(analytics.cancelledValue) }}
            </div>
          </article>
          <article class="ha__metric ha__metric--danger">
            <div class="ha__metric-label">Failed payments</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.failedPayments) }}
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Refunds</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.refundCount) }}
            </div>
            <div class="ha__metric-foot">
              {{ formatCurrency(analytics.refundTotal) }}
            </div>
          </article>
          <article class="ha__metric ha__metric--ok">
            <div class="ha__metric-label">Conversion</div>
            <div class="ha__metric-value">
              {{ analytics.conversionRate || 0 }}%
            </div>
            <div class="ha__metric-foot">Paid vs failed</div>
          </article>
        </div>
      </section>

      <section class="ha__section">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Vouchers &amp; discounts</h3>
          <p class="ha__section-sub">Promo impact on ticket revenue</p>
        </div>
        <div class="ha__voucher-grid">
          <article class="ha__voucher">
            <div class="ha__voucher-label">With voucher</div>
            <div class="ha__voucher-value">
              {{ formatCurrency(analytics.revenueWithVoucher) }}
            </div>
            <div class="ha__voucher-bar">
              <div
                class="ha__voucher-fill ha__voucher-fill--a"
                :style="{ width: voucherShare.with + '%' }"
              />
            </div>
            <div class="ha__metric-foot">
              {{ formatNumber(analytics.voucherOrders) }} orders ·
              {{ voucherShare.with }}%
            </div>
          </article>
          <article class="ha__voucher">
            <div class="ha__voucher-label">Without voucher</div>
            <div class="ha__voucher-value">
              {{ formatCurrency(analytics.revenueWithoutVoucher) }}
            </div>
            <div class="ha__voucher-bar">
              <div
                class="ha__voucher-fill ha__voucher-fill--b"
                :style="{ width: voucherShare.without + '%' }"
              />
            </div>
            <div class="ha__metric-foot">{{ voucherShare.without }}% of gross</div>
          </article>
          <article class="ha__voucher">
            <div class="ha__voucher-label">Discount given</div>
            <div class="ha__voucher-value">
              {{ formatCurrency(analytics.discountTotal) }}
            </div>
            <div class="ha__metric-foot">Total voucher / pass discount</div>
          </article>
        </div>
      </section>

      <section class="ha__section">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Audience &amp; attendance</h3>
          <p class="ha__section-sub">Footfall and check-in quality</p>
        </div>
        <div class="ha__metric-grid ha__metric-grid--4">
          <article class="ha__metric">
            <div class="ha__metric-label">User footfall</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.footfall) }}
            </div>
            <div class="ha__metric-foot">Seats scanned</div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Check-in rate</div>
            <div class="ha__metric-value">
              {{ analytics.checkInRate || 0 }}%
            </div>
            <div class="ha__meter">
              <div
                class="ha__meter-fill ha__meter-fill--ok"
                :style="{ width: (analytics.checkInRate || 0) + '%' }"
              />
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Est. no-show</div>
            <div class="ha__metric-value">
              {{ analytics.noShowEstimate || 0 }}%
            </div>
            <div class="ha__meter">
              <div
                class="ha__meter-fill ha__meter-fill--warn"
                :style="{ width: (analytics.noShowEstimate || 0) + '%' }"
              />
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Paid orders</div>
            <div class="ha__metric-value">
              {{ formatNumber(analytics.paidOrders) }}
            </div>
          </article>
        </div>
      </section>

      <section class="ha__section">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Booking behaviour</h3>
          <p class="ha__section-sub">
            How early people buy and when demand peaks
          </p>
        </div>
        <div class="ha__metric-grid ha__metric-grid--4">
          <article class="ha__metric">
            <div class="ha__metric-label">Avg lead time</div>
            <div class="ha__metric-value">
              {{
                (analytics.bookingLeadTime &&
                  analytics.bookingLeadTime.avgLeadDays) ||
                0
              }}d
            </div>
            <div class="ha__metric-foot">Days before event</div>
          </article>
          <article class="ha__metric ha__metric--warn">
            <div class="ha__metric-label">Last-minute</div>
            <div class="ha__metric-value">
              {{
                (analytics.bookingLeadTime &&
                  analytics.bookingLeadTime.lastMinuteRate) ||
                0
              }}%
            </div>
            <div class="ha__metric-foot">
              {{
                formatNumber(
                  (analytics.bookingLeadTime &&
                    analytics.bookingLeadTime.lastMinuteOrders) ||
                    0,
                )
              }}
              orders ≤ 1 day
            </div>
          </article>
          <article class="ha__metric ha__metric--ok">
            <div class="ha__metric-label">Early bird</div>
            <div class="ha__metric-value">
              {{
                (analytics.bookingLeadTime &&
                  analytics.bookingLeadTime.earlyBirdRate) ||
                0
              }}%
            </div>
            <div class="ha__metric-foot">
              {{
                formatNumber(
                  (analytics.bookingLeadTime &&
                    analytics.bookingLeadTime.earlyBirdOrders) ||
                    0,
                )
              }}
              orders ≥ 14 days
            </div>
          </article>
          <article class="ha__metric">
            <div class="ha__metric-label">Peak booking hour</div>
            <div class="ha__metric-value">
              {{
                (analytics.hourlySales &&
                  analytics.hourlySales.peakHour &&
                  analytics.hourlySales.peakHour.label) ||
                "—"
              }}
            </div>
            <div class="ha__metric-foot">
              {{
                formatCurrency(
                  (analytics.hourlySales &&
                    analytics.hourlySales.peakHour &&
                    analytics.hourlySales.peakHour.revenue) ||
                    0,
                )
              }}
            </div>
          </article>
        </div>

        <div class="ha__split ha__split--tight">
          <div class="ha__section ha__section--panel">
            <div class="ha__section-head">
              <h3 class="ha__section-title">Weekend vs weekday</h3>
              <p class="ha__section-sub">Sales by booking day</p>
            </div>
            <div class="ha__stack-bars">
              <div class="ha__stack-row">
                <span class="ha__stack-label">Weekend</span>
                <div class="ha__stack-track">
                  <div
                    class="ha__stack-fill ha__stack-fill--a"
                    :style="{ width: weekendShare.weekend + '%' }"
                  />
                </div>
                <span class="ha__stack-val">
                  {{
                    formatCurrency(
                      (analytics.weekendWeekday &&
                        analytics.weekendWeekday.weekendRevenue) ||
                        0,
                    )
                  }}
                </span>
              </div>
              <div class="ha__stack-row">
                <span class="ha__stack-label">Weekday</span>
                <div class="ha__stack-track">
                  <div
                    class="ha__stack-fill ha__stack-fill--b"
                    :style="{ width: weekendShare.weekday + '%' }"
                  />
                </div>
                <span class="ha__stack-val">
                  {{
                    formatCurrency(
                      (analytics.weekendWeekday &&
                        analytics.weekendWeekday.weekdayRevenue) ||
                        0,
                    )
                  }}
                </span>
              </div>
            </div>
          </div>

          <div class="ha__section ha__section--panel">
            <div class="ha__section-head">
              <h3 class="ha__section-title">Guest vs registered</h3>
              <p class="ha__section-sub">Checkout account mix</p>
            </div>
            <div class="ha__stack-bars">
              <div class="ha__stack-row">
                <span class="ha__stack-label">Guest</span>
                <div class="ha__stack-track">
                  <div
                    class="ha__stack-fill ha__stack-fill--a"
                    :style="{ width: guestShare.guest + '%' }"
                  />
                </div>
                <span class="ha__stack-val">
                  {{
                    formatNumber(
                      (analytics.guestSplit &&
                        analytics.guestSplit.guestOrders) ||
                        0,
                    )
                  }}
                </span>
              </div>
              <div class="ha__stack-row">
                <span class="ha__stack-label">Registered</span>
                <div class="ha__stack-track">
                  <div
                    class="ha__stack-fill ha__stack-fill--b"
                    :style="{ width: guestShare.registered + '%' }"
                  />
                </div>
                <span class="ha__stack-val">
                  {{
                    formatNumber(
                      (analytics.guestSplit &&
                        analytics.guestSplit.registeredOrders) ||
                        0,
                    )
                  }}
                </span>
              </div>
            </div>
            <div class="ha__metric-foot mt-2">
              Free tickets:
              {{
                formatNumber(
                  (analytics.freePaidSplit &&
                    analytics.freePaidSplit.freeTickets) ||
                    0,
                )
              }}
              · Paid tickets:
              {{
                formatNumber(
                  (analytics.freePaidSplit &&
                    analytics.freePaidSplit.paidTickets) ||
                    0,
                )
              }}
            </div>
          </div>
        </div>
      </section>

      <div class="ha__split">
        <section class="ha__section ha__section--panel">
          <div class="ha__section-head">
            <h3 class="ha__section-title">Sales by channel</h3>
            <p class="ha__section-sub">Website vs box office</p>
          </div>
          <ul class="ha__rank-list">
            <li
              v-for="(row, idx) in analytics.channelBreakdown || []"
              :key="'ch-' + idx"
              class="ha__rank-item"
            >
              <span class="ha__rank-num">{{ idx + 1 }}</span>
              <span class="ha__rank-name">{{ row.channel }}</span>
              <span class="ha__rank-rev">{{ formatCurrency(row.revenue) }}</span>
            </li>
            <li
              v-if="!(analytics.channelBreakdown || []).length"
              class="ha__empty"
            >
              No channel data
            </li>
          </ul>
        </section>

        <section class="ha__section ha__section--panel">
          <div class="ha__section-head">
            <h3 class="ha__section-title">Payment modes</h3>
            <p class="ha__section-sub">How customers paid</p>
          </div>
          <ul class="ha__rank-list">
            <li
              v-for="(row, idx) in analytics.paymentModeBreakdown || []"
              :key="'pm-' + idx"
              class="ha__rank-item"
            >
              <span class="ha__rank-num">{{ idx + 1 }}</span>
              <span class="ha__rank-name">{{ row.mode }}</span>
              <span class="ha__rank-rev">{{ formatCurrency(row.revenue) }}</span>
            </li>
            <li
              v-if="!(analytics.paymentModeBreakdown || []).length"
              class="ha__empty"
            >
              No payment data
            </li>
          </ul>
        </section>
      </div>

      <section class="ha__section ha__section--panel">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Seat / ticket types</h3>
          <p class="ha__section-sub">Which categories sell best</p>
        </div>
        <div class="ha__seat-grid">
          <div
            v-for="(row, idx) in analytics.seatTypeBreakdown || []"
            :key="'seat-' + idx"
            class="ha__seat-card"
          >
            <div class="ha__seat-name">{{ row.seatType }}</div>
            <div class="ha__seat-tickets">
              {{ formatNumber(row.tickets) }} tickets
            </div>
            <div class="ha__voucher-bar">
              <div
                class="ha__voucher-fill ha__voucher-fill--b"
                :style="{
                  width: (row.tickets / seatTypeMax) * 100 + '%',
                }"
              />
            </div>
            <div class="ha__metric-foot">{{ formatCurrency(row.revenue) }}</div>
          </div>
          <div
            v-if="!(analytics.seatTypeBreakdown || []).length"
            class="ha__empty"
          >
            No seat-type breakdown for this period
          </div>
        </div>
      </section>

      <section class="ha__section ha__section--panel">
        <div class="ha__section-head">
          <h3 class="ha__section-title">Hourly sales heat</h3>
          <p class="ha__section-sub">When buyers complete checkout</p>
        </div>
        <div class="ha__hour-grid">
          <div
            v-for="h in activeHours"
            :key="'hour-' + h.hour"
            class="ha__hour-cell"
            :title="formatCurrency(h.revenue)"
          >
            <div
              class="ha__hour-bar"
              :style="{
                opacity: Math.max(h.revenue / hourlyMax, 0.08),
                height: Math.max((h.revenue / hourlyMax) * 100, 8) + '%',
              }"
            />
            <span class="ha__hour-label">{{ h.hour }}</span>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import {
  GetEventListService,
  GetOrganizerAnalyticsService,
} from "@/apiServices/MasterServices";
import moment from "moment";

const emptyAnalytics = () => ({
  periodLabel: "",
  revenueGross: 0,
  revenueNet: 0,
  platformFees: 0,
  ticketsSold: 0,
  avgOrderValue: 0,
  avgTicketsPerOrder: 0,
  uniqueBuyers: 0,
  repeatBuyerRate: 0,
  cancelledBookings: 0,
  cancelledValue: 0,
  failedPayments: 0,
  refundCount: 0,
  refundTotal: 0,
  footfall: 0,
  checkInRate: 0,
  noShowEstimate: 0,
  revenueWithVoucher: 0,
  revenueWithoutVoucher: 0,
  voucherOrders: 0,
  discountTotal: 0,
  conversionRate: 0,
  paidOrders: 0,
  salesDeltaPct: 0,
  salesTrend: [],
  topEvents: [],
  topPerformingEvent: null,
  channelBreakdown: [],
  paymentModeBreakdown: [],
  weekendWeekday: {
    weekendRevenue: 0,
    weekdayRevenue: 0,
    weekendTickets: 0,
    weekdayTickets: 0,
  },
  guestSplit: {
    guestOrders: 0,
    registeredOrders: 0,
    guestRevenue: 0,
    registeredRevenue: 0,
  },
  seatTypeBreakdown: [],
  bookingLeadTime: {
    avgLeadDays: 0,
    lastMinuteOrders: 0,
    earlyBirdOrders: 0,
    lastMinuteRate: 0,
    earlyBirdRate: 0,
  },
  freePaidSplit: {
    freeTickets: 0,
    paidTickets: 0,
    freeOrders: 0,
  },
  occupancy: {
    upcomingSchedules: 0,
    totalCapacity: 0,
    soldSeats: 0,
    remainingSeats: 0,
    sellThroughRate: 0,
    nearSellOut: [],
  },
  hourlySales: {
    hourly: [],
    peakHour: null,
  },
  salesSummary: {
    rows: [],
    totals: {
      totalTransactions: 0,
      totalSeatsBooked: 0,
      voucherBookings: 0,
      transactionAmount: 0,
      totalRefunds: 0,
      refundAmount: 0,
      finalAmount: 0,
    },
  },
  alerts: [],
});

export default {
  name: "HomeAnalytics",
  components: {
    BSpinner,
    flatPickr,
  },
  props: {
    events: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      preset: "this_month",
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      selectedEventId: "",
      eventOptions: [],
      eventsLoading: false,
      analytics: emptyAnalytics(),
      dateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      presetOptions: [
        { value: "today", text: "Today" },
        { value: "last_7_days", text: "Weekly" },
        { value: "last_30_days", text: "30 days" },
        { value: "last_month", text: "Last month" },
        { value: "this_month", text: "This month" },
        { value: "last_90_days", text: "90 days" },
        { value: "custom", text: "Custom" },
      ],
    };
  },
  computed: {
    periodRangeLabel() {
      const fromApiStart = this.analytics && this.analytics.startDate;
      const fromApiEnd = this.analytics && this.analytics.endDate;
      const start = fromApiStart
        ? moment(fromApiStart).format("DD MMM YYYY")
        : this.startDate
          ? moment(this.startDate).format("DD MMM YYYY")
          : "—";
      const end = fromApiEnd
        ? moment(fromApiEnd).format("DD MMM YYYY")
        : this.endDate
          ? moment(this.endDate).format("DD MMM YYYY")
          : "—";
      return `${start} – ${end}`;
    },
    salesSummaryRows() {
      return (
        (this.analytics.salesSummary && this.analytics.salesSummary.rows) ||
        []
      );
    },
    salesSummaryTotals() {
      return (
        (this.analytics.salesSummary && this.analytics.salesSummary.totals) || {
          totalTransactions: 0,
          totalSeatsBooked: 0,
          voucherBookings: 0,
          transactionAmount: 0,
          totalRefunds: 0,
          refundAmount: 0,
          finalAmount: 0,
        }
      );
    },
    salesSummarySubtitle() {
      const client = "RAINBOW";
      const start = this.startDate
        ? moment(this.startDate).format("DD/MM/YYYY")
        : "—";
      const end = this.endDate
        ? moment(this.endDate).format("DD/MM/YYYY")
        : "—";
      const sameDay = this.startDate && this.startDate === this.endDate;
      const datePart = sameDay ? start : `${start} - ${end}`;
      return `${client} | Booking period: ${datePart} (12:00 AM - 11:59 PM) · past & live events`;
    },
    salesTrendMax() {
      const t = this.analytics && this.analytics.salesTrend;
      if (!t || !t.length) return 1;
      return Math.max(...t.map((d) => Number(d.value) || 0), 1);
    },
    voucherShare() {
      const withV = Number(this.analytics.revenueWithVoucher) || 0;
      const without = Number(this.analytics.revenueWithoutVoucher) || 0;
      const total = withV + without;
      if (!total) return { with: 0, without: 0 };
      return {
        with: Math.round((withV / total) * 100),
        without: Math.round((without / total) * 100),
      };
    },
    weekendShare() {
      const w =
        (this.analytics.weekendWeekday &&
          Number(this.analytics.weekendWeekday.weekendRevenue)) ||
        0;
      const d =
        (this.analytics.weekendWeekday &&
          Number(this.analytics.weekendWeekday.weekdayRevenue)) ||
        0;
      const total = w + d;
      if (!total) return { weekend: 0, weekday: 0 };
      return {
        weekend: Math.round((w / total) * 100),
        weekday: Math.round((d / total) * 100),
      };
    },
    guestShare() {
      const g =
        (this.analytics.guestSplit &&
          Number(this.analytics.guestSplit.guestOrders)) ||
        0;
      const r =
        (this.analytics.guestSplit &&
          Number(this.analytics.guestSplit.registeredOrders)) ||
        0;
      const total = g + r;
      if (!total) return { guest: 0, registered: 0 };
      return {
        guest: Math.round((g / total) * 100),
        registered: Math.round((r / total) * 100),
      };
    },
    seatTypeMax() {
      const rows = this.analytics.seatTypeBreakdown || [];
      return Math.max(...rows.map((r) => Number(r.tickets) || 0), 1);
    },
    hourlyMax() {
      const rows =
        (this.analytics.hourlySales && this.analytics.hourlySales.hourly) || [];
      return Math.max(...rows.map((r) => Number(r.revenue) || 0), 1);
    },
    activeHours() {
      const rows =
        (this.analytics.hourlySales && this.analytics.hourlySales.hourly) || [];
      if (!rows.length) return [];
      return rows.filter((h) => h.hour >= 8 && h.hour <= 23);
    },
  },
  watch: {
    events: {
      immediate: true,
      handler(list) {
        this.mergeEventOptions(list);
      },
    },
  },
  mounted() {
    this.loadEventOptions();
    this.loadAnalytics();
  },
  methods: {
    mergeEventOptions(list) {
      if (!Array.isArray(list) || !list.length) return;
      const map = new Map();
      [...this.eventOptions, ...list].forEach((e) => {
        if (e && e.event_id != null) map.set(e.event_id, e);
      });
      this.eventOptions = Array.from(map.values()).sort((a, b) =>
        String(a.event_name || "").localeCompare(String(b.event_name || "")),
      );
    },
    formatCurrency(value) {
      if (value == null || Number.isNaN(Number(value))) return "—";
      try {
        return new Intl.NumberFormat("en-JO", {
          style: "currency",
          currency: "JOD",
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }).format(Number(value));
      } catch {
        return `JOD ${Number(value).toLocaleString("en-JO", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        })}`;
      }
    },
    formatJod(value, currency) {
      const amount = Number(value || 0).toLocaleString("en-JO", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });
      return `${amount} ${currency || "JOD"}`;
    },
    formatNumber(value) {
      if (value == null || Number.isNaN(Number(value))) return "—";
      return Number(value).toLocaleString("en-JO");
    },
    formatCompact(value) {
      const n = Number(value) || 0;
      if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
      return String(Math.round(n));
    },
    selectPreset(value) {
      this.preset = value;
      this.onPresetChange();
    },
    onPresetChange() {
      const end = moment();
      if (this.preset === "today") {
        this.startDate = end.clone().startOf("day").format("YYYY-MM-DD");
        this.endDate = end.format("YYYY-MM-DD");
      } else if (this.preset === "last_7_days") {
        this.startDate = end.clone().subtract(6, "days").format("YYYY-MM-DD");
        this.endDate = end.format("YYYY-MM-DD");
      } else if (this.preset === "last_30_days") {
        this.startDate = end.clone().subtract(29, "days").format("YYYY-MM-DD");
        this.endDate = end.format("YYYY-MM-DD");
      } else if (this.preset === "last_90_days") {
        this.startDate = end.clone().subtract(89, "days").format("YYYY-MM-DD");
        this.endDate = end.format("YYYY-MM-DD");
      } else if (this.preset === "this_month") {
        this.startDate = end.clone().startOf("month").format("YYYY-MM-DD");
        this.endDate = end.format("YYYY-MM-DD");
      } else if (this.preset === "last_month") {
        this.startDate = end
          .clone()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        this.endDate = end
          .clone()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD");
      }
      if (this.preset !== "custom") {
        this.loadAnalytics();
      }
    },
    resetFilters() {
      this.preset = "this_month";
      this.selectedEventId = "";
      this.onPresetChange();
    },
    async loadEventOptions() {
      this.eventsLoading = true;
      try {
        let org_id;
        if (
          window.location.hostname === "dashboard.therainbowtheater.com" ||
          window.location.hostname === "localhost"
        ) {
          org_id = 2;
        }
        const response = await GetEventListService({
          limit: 5000,
          org_id,
          selectedEventType: "",
        });
        if (response.data && response.data.status) {
          const records = response.data.Records;
          const list = Array.isArray(records)
            ? records
            : (records && records.data) || [];
          this.mergeEventOptions(list);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.eventsLoading = false;
      }
    },
    async loadAnalytics() {
      this.loading = true;
      try {
        const params = {
          preset: this.preset,
        };
        if (this.preset === "custom") {
          params.start_date = this.startDate;
          params.end_date = this.endDate;
        }
        if (this.selectedEventId) {
          params.event_id = this.selectedEventId;
        }
        const response = await GetOrganizerAnalyticsService(params);
        if (response.data.status) {
          this.analytics = {
            ...emptyAnalytics(),
            ...(response.data.Records || {}),
          };
        } else {
          this.analytics = emptyAnalytics();
        }
      } catch (error) {
        console.log(error);
        this.analytics = emptyAnalytics();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ha {
  --ha-ink: #1a2332;
  --ha-muted: #6b7785;
  --ha-line: rgba(26, 35, 50, 0.08);
  --ha-surface: #ffffff;
  --ha-bg: #f3f5f8;
  --ha-accent: #e85d04;
  --ha-accent-soft: rgba(232, 93, 4, 0.1);
  --ha-teal: #0f766e;
  --ha-teal-soft: rgba(15, 118, 110, 0.1);
  --ha-warn: #c2410c;
  --ha-danger: #b91c1c;
  --ha-ok: #15803d;
  --ha-radius: 14px;
  --ha-shadow: 0 1px 0 rgba(26, 35, 50, 0.04);

  color: var(--ha-ink);
  background:
    radial-gradient(
      1200px 420px at 8% -10%,
      rgba(232, 93, 4, 0.07),
      transparent 55%
    ),
    radial-gradient(
      900px 380px at 100% 0%,
      rgba(15, 118, 110, 0.06),
      transparent 50%
    ),
    var(--ha-bg);
  border: 1px solid var(--ha-line);
  border-radius: 18px;
  padding: 1.25rem 1.25rem 1.5rem;
  animation: ha-fade 0.35s ease;
}

.ha__dev-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  border: 1px dashed rgba(232, 93, 4, 0.35);
  background: rgba(232, 93, 4, 0.08);
}

.ha__dev-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: var(--ha-accent);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.ha__dev-text {
  color: #9a3412;
  font-size: 0.84rem;
  font-weight: 600;
  line-height: 1.35;
}

@keyframes ha-fade {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.ha__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.1rem;
}

.ha__eyebrow {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ha-accent);
}

.ha__title {
  margin: 0.15rem 0 0.2rem;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.ha__lede {
  margin: 0;
  max-width: 36rem;
  color: var(--ha-muted);
  font-size: 0.92rem;
}

.ha__hero-meta {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.ha__chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--ha-ink);
}

.ha__chip--muted {
  color: var(--ha-muted);
  background: transparent;
}

.ha__chip--dates {
  color: var(--ha-teal);
  border-color: rgba(15, 118, 110, 0.2);
  background: var(--ha-teal-soft);
}

.ha__range-note {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem 0.65rem;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  background: var(--ha-bg);
  border: 1px solid var(--ha-line);
}

.ha__range-note-label {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ha-muted);
}

.ha__range-note-value {
  font-size: 0.88rem;
  font-weight: 800;
  color: var(--ha-ink);
}

.ha__toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.85rem;
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  border-radius: var(--ha-radius);
  box-shadow: var(--ha-shadow);
  margin-bottom: 1.15rem;
}

.ha__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.ha__preset {
  border: 1px solid transparent;
  background: var(--ha-bg);
  color: var(--ha-muted);
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.4rem 0.75rem;
  border-radius: 10px;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
  cursor: pointer;
}

.ha__preset:hover {
  color: var(--ha-ink);
  border-color: var(--ha-line);
}

.ha__preset--active {
  background: var(--ha-accent);
  color: #fff;
  border-color: var(--ha-accent);
}

.ha__toolbar-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  align-items: center;
}

.ha__date-pair {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.ha__date-sep {
  color: var(--ha-muted);
  font-size: 0.8rem;
  font-weight: 600;
}

.ha__input {
  min-width: 124px;
  border-radius: 10px !important;
  border-color: var(--ha-line) !important;
  font-size: 0.85rem;
  height: 38px;
}

.ha__event-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 220px;
  max-width: 360px;
}

.ha__field-label {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ha-muted);
}

.ha__event-native {
  width: 100%;
  min-width: 220px;
  cursor: pointer;
  background: #fff;
}

.ha__btn {
  border: 1px solid var(--ha-line);
  border-radius: 10px;
  height: 38px;
  padding: 0 0.95rem;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease, opacity 0.15s ease;
}

.ha__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ha__btn:not(:disabled):active {
  transform: translateY(1px);
}

.ha__btn--primary {
  background: var(--ha-ink);
  border-color: var(--ha-ink);
  color: #fff;
}

.ha__btn--primary:hover:not(:disabled) {
  background: #0f1724;
}

.ha__btn--ghost {
  background: transparent;
  color: var(--ha-muted);
}

.ha__btn--ghost:hover:not(:disabled) {
  color: var(--ha-ink);
  background: var(--ha-bg);
}

.ha__loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--ha-muted);
  font-size: 0.9rem;
  padding: 2rem 0.5rem;
}

.ha__loading-bar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--ha-line);
  border-top-color: var(--ha-accent);
  animation: ha-spin 0.7s linear infinite;
}

@keyframes ha-spin {
  to {
    transform: rotate(360deg);
  }
}

.ha__insights {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1.1rem;
}

.ha__insight {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.35;
  animation: ha-fade 0.4s ease;
}

.ha__insight-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex-shrink: 0;
}

.ha__insight--warning {
  background: #fff7ed;
  color: #9a3412;
}
.ha__insight--warning .ha__insight-dot {
  background: #ea580c;
}

.ha__insight--info {
  background: #f0fdfa;
  color: #115e59;
}
.ha__insight--info .ha__insight-dot {
  background: var(--ha-teal);
}

.ha__insight--danger {
  background: #fef2f2;
  color: #991b1b;
}
.ha__insight--danger .ha__insight-dot {
  background: var(--ha-danger);
}

.ha__insight--secondary {
  background: #f8fafc;
  color: #475569;
}
.ha__insight--secondary .ha__insight-dot {
  background: #94a3b8;
}

.ha__sales-card {
  margin-bottom: 1.25rem;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--ha-line);
  background: #fff;
  box-shadow: var(--ha-shadow);
}

.ha__sales-card-head {
  background: #1b365d;
  color: #fff;
  padding: 0.9rem 1.1rem;
}

.ha__sales-card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
}

.ha__sales-card-sub {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  opacity: 0.88;
}

.ha__sales-table-wrap {
  overflow-x: auto;
}

.ha__sales-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
  font-size: 0.8rem;
}

.ha__sales-table th,
.ha__sales-table td {
  border: 1px solid #d9dee7;
  padding: 0.55rem 0.65rem;
  text-align: left;
  vertical-align: middle;
}

.ha__sales-table th {
  background: #f7f8fb;
  font-weight: 800;
  color: #334155;
  white-space: nowrap;
}

.ha__sales-table td {
  color: #1e293b;
}

.ha__sales-name {
  max-width: 280px;
  font-weight: 650;
}

.ha__sales-table tfoot td {
  background: #eef1f6;
  font-weight: 800;
}

.ha__sales-table .ha__num {
  text-align: right;
  white-space: nowrap;
}

.ha__sales-empty {
  text-align: center;
  color: var(--ha-muted);
  padding: 1.2rem !important;
}

.ha__section {
  margin-bottom: 1.25rem;
}

.ha__section--panel {
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  border-radius: var(--ha-radius);
  padding: 1rem 1.05rem 1.1rem;
  box-shadow: var(--ha-shadow);
  margin-bottom: 0;
}

.ha__section-head {
  margin-bottom: 0.85rem;
}

.ha__section-title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.ha__section-sub {
  margin: 0.15rem 0 0;
  color: var(--ha-muted);
  font-size: 0.8rem;
}

.ha__revenue-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
}

.ha__stat {
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  border-radius: var(--ha-radius);
  padding: 1rem 1.05rem;
  box-shadow: var(--ha-shadow);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.ha__stat:hover {
  transform: translateY(-1px);
  border-color: rgba(26, 35, 50, 0.14);
}

.ha__stat--hero {
  background: linear-gradient(
    145deg,
    #1a2332 0%,
    #243044 55%,
    #1f3a3a 100%
  );
  border-color: transparent;
  color: #fff;
}

.ha__stat--hero .ha__stat-label {
  color: rgba(255, 255, 255, 0.68);
}

.ha__stat-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ha-muted);
}

.ha__stat-value {
  margin-top: 0.4rem;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  word-break: break-word;
}

.ha__stat-value--xl {
  font-size: clamp(1.55rem, 2.4vw, 2rem);
}

.ha__stat-delta {
  margin-top: 0.55rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.ha__stat-delta--up {
  color: #86efac;
}

.ha__stat-delta--down {
  color: #fdba74;
}

.ha__metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.7rem;
}

.ha__metric-grid--4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.ha__metric {
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  border-radius: var(--ha-radius);
  padding: 0.9rem 0.95rem;
  box-shadow: var(--ha-shadow);
  position: relative;
  overflow: hidden;
}

.ha__metric::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: rgba(26, 35, 50, 0.12);
}

.ha__metric--warn::before {
  background: var(--ha-warn);
}
.ha__metric--danger::before {
  background: var(--ha-danger);
}
.ha__metric--ok::before {
  background: var(--ha-ok);
}

.ha__metric-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--ha-muted);
  letter-spacing: 0.02em;
}

.ha__metric-value {
  margin-top: 0.35rem;
  font-size: 1.28rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  word-break: break-word;
}

.ha__metric-foot {
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: var(--ha-muted);
  font-weight: 600;
}

.ha__split {
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.ha__split--tight {
  margin-top: 0.75rem;
  margin-bottom: 0;
  grid-template-columns: 1fr 1fr;
}

.ha__near-list {
  margin-top: 0.75rem;
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  border-radius: var(--ha-radius);
  padding: 0.75rem 0.9rem;
}

.ha__near-title {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ha-muted);
  margin-bottom: 0.45rem;
}

.ha__near-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.35rem 0;
  border-top: 1px solid var(--ha-line);
  font-size: 0.84rem;
}

.ha__near-row:first-of-type {
  border-top: 0;
}

.ha__near-name {
  font-weight: 700;
}

.ha__near-meta {
  color: var(--ha-muted);
  font-weight: 600;
  white-space: nowrap;
}

.ha__stack-bars {
  display: grid;
  gap: 0.7rem;
}

.ha__stack-row {
  display: grid;
  grid-template-columns: 84px 1fr auto;
  gap: 0.55rem;
  align-items: center;
}

.ha__stack-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--ha-muted);
}

.ha__stack-track {
  height: 8px;
  border-radius: 999px;
  background: var(--ha-bg);
  overflow: hidden;
}

.ha__stack-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.45s ease;
}

.ha__stack-fill--a {
  background: var(--ha-accent);
}

.ha__stack-fill--b {
  background: var(--ha-teal);
}

.ha__stack-val {
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.ha__seat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.65rem;
}

.ha__seat-card {
  border: 1px solid var(--ha-line);
  border-radius: 12px;
  padding: 0.75rem 0.85rem;
  background: var(--ha-bg);
}

.ha__seat-name {
  font-weight: 800;
  font-size: 0.9rem;
}

.ha__seat-tickets {
  margin-top: 0.15rem;
  font-size: 0.78rem;
  color: var(--ha-muted);
  font-weight: 650;
}

.ha__hour-grid {
  display: grid;
  grid-template-columns: repeat(16, minmax(0, 1fr));
  gap: 0.3rem;
  align-items: end;
  height: 120px;
}

.ha__hour-cell {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

.ha__hour-bar {
  width: 100%;
  max-width: 18px;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(180deg, #f97316, #0f766e);
  min-height: 4px;
}

.ha__hour-label {
  font-size: 0.58rem;
  font-weight: 700;
  color: var(--ha-muted);
}

.ha__trend {
  display: flex;
  align-items: flex-end;
  gap: 0.3rem;
  height: 170px;
  padding-top: 0.35rem;
}

.ha__trend-col {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}

.ha__trend-value {
  font-size: 0.62rem;
  font-weight: 700;
  color: var(--ha-muted);
  margin-bottom: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.ha__trend-col:hover .ha__trend-value {
  opacity: 1;
}

.ha__trend-track {
  width: 100%;
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 0;
}

.ha__trend-bar {
  width: 100%;
  max-width: 28px;
  border-radius: 8px 8px 3px 3px;
  background: linear-gradient(180deg, #f97316 0%, #ea580c 55%, #0f766e 140%);
  min-height: 4px;
  transition: height 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.ha__trend-label {
  margin-top: 0.4rem;
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--ha-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.ha__spotlight {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.85rem;
  border-radius: 12px;
  background: var(--ha-teal-soft);
  border: 1px solid rgba(15, 118, 110, 0.15);
  margin-bottom: 0.75rem;
}

.ha__spotlight-rank {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--ha-teal);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.ha__spotlight-name {
  font-weight: 800;
  font-size: 1rem;
  line-height: 1.25;
}

.ha__spotlight-stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.25rem;
  color: var(--ha-muted);
  font-size: 0.8rem;
  font-weight: 650;
}

.ha__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--ha-muted);
}

.ha__empty {
  color: var(--ha-muted);
  font-size: 0.88rem;
  padding: 0.5rem 0 0.85rem;
}

.ha__rank-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.35rem;
}

.ha__rank-item {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 0.55rem;
  align-items: center;
  padding: 0.45rem 0.35rem;
  border-radius: 10px;
  transition: background 0.15s ease;
}

.ha__rank-item:hover {
  background: var(--ha-bg);
}

.ha__rank-num {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: var(--ha-bg);
  color: var(--ha-muted);
  font-size: 0.72rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ha__rank-name {
  font-size: 0.84rem;
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ha__rank-rev {
  font-size: 0.8rem;
  font-weight: 800;
  color: var(--ha-ink);
  white-space: nowrap;
}

.ha__voucher-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.7rem;
}

.ha__voucher {
  background: var(--ha-surface);
  border: 1px solid var(--ha-line);
  border-radius: var(--ha-radius);
  padding: 0.95rem 1rem;
  box-shadow: var(--ha-shadow);
}

.ha__voucher-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--ha-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ha__voucher-value {
  margin-top: 0.35rem;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.ha__voucher-bar {
  height: 6px;
  border-radius: 999px;
  background: var(--ha-bg);
  margin: 0.7rem 0 0.45rem;
  overflow: hidden;
}

.ha__voucher-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.5s ease;
}

.ha__voucher-fill--a {
  background: var(--ha-accent);
}

.ha__voucher-fill--b {
  background: var(--ha-teal);
}

.ha__meter {
  height: 6px;
  border-radius: 999px;
  background: var(--ha-bg);
  margin-top: 0.65rem;
  overflow: hidden;
}

.ha__meter-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.45s ease;
}

.ha__meter-fill--ok {
  background: var(--ha-ok);
}

.ha__meter-fill--warn {
  background: #d97706;
}

@media (max-width: 991px) {
  .ha__revenue-grid,
  .ha__split,
  .ha__split--tight,
  .ha__voucher-grid {
    grid-template-columns: 1fr;
  }

  .ha__metric-grid,
  .ha__metric-grid--4 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 575px) {
  .ha {
    padding: 1rem;
  }

  .ha__metric-grid,
  .ha__metric-grid--4 {
    grid-template-columns: 1fr;
  }

  .ha__event-select {
    max-width: none;
    width: 100%;
  }

  .ha__event-field {
    max-width: none;
    width: 100%;
  }

  .ha__hour-grid {
    grid-template-columns: repeat(8, minmax(0, 1fr));
    height: 160px;
  }

  .ha__stack-row {
    grid-template-columns: 72px 1fr;
  }

  .ha__stack-val {
    grid-column: 2;
  }

  .ha__date-pair {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
